import { lazy } from 'react';

const RELOAD_KEY = 'pa_chunk_reload';
const wait = (ms) => new Promise((r) => { setTimeout(r, ms); });

/*
 * React.lazy with resilience for weak or patchy connections:
 *  - a failed page download is retried twice (after 0.6 s and 2 s) before giving up;
 *  - if it still fails because a new version of the site was deployed (old file names gone),
 *    the page reloads once to pick up the new version;
 *  - Component.preload() fetches the page ahead of time (used for likely next pages on fast connections).
 */
export default function lazyRetry(factory) {
  let pending;
  const load = () => {
    if (!pending) {
      pending = (async () => {
        let lastErr;
        for (const delay of [0, 600, 2000]) {
          if (delay) await wait(delay);
          try {
            const mod = await factory();
            try { sessionStorage.removeItem(RELOAD_KEY); } catch { /* storage unavailable */ }
            return mod;
          } catch (err) { lastErr = err; }
        }
        const isChunk = /chunk|dynamically imported module|Importing a module script failed/i.test(String(lastErr?.message || lastErr?.name));
        let reloaded = false;
        try { reloaded = sessionStorage.getItem(RELOAD_KEY) === '1'; } catch { /* ignore */ }
        if (isChunk && !reloaded && typeof navigator !== 'undefined' && navigator.onLine !== false) {
          try { sessionStorage.setItem(RELOAD_KEY, '1'); } catch { /* ignore */ }
          window.location.reload();
          return new Promise(() => {}); // keep showing the loader while the page reloads
        }
        pending = undefined; // allow a later retry, e.g. when the connection comes back
        throw lastErr;
      })();
    }
    return pending;
  };
  const Component = lazy(load);
  Component.preload = load;
  return Component;
}
